import { z } from "zod";
import { BusinessVerticalSchema, type BusinessVertical } from "./vertical";

// ---------------------------------------------------------------------------
// Organisation (onboarding step 3, GRA-171)
//
// The four facts the new onboarding asks for: the unit's name, its number
// (store number for a grocery store, organisation number for a sales
// company), the headcount, and the chain (DVH) or the kind of selling (Sales).
// Stored on `Businesses/{id}.organisation`. For DVH the store itself is a root
// sales team with `storeMeta.chain` (GRA-97); `storeTeamId` points at it.
// ---------------------------------------------------------------------------

export const DVH_CHAINS = [
  "ica_nara",
  "ica_supermarket",
  "ica_kvantum",
  "ica_maxi",
  "other",
] as const;
export type DvhChain = (typeof DVH_CHAINS)[number];
export const DvhChainSchema = z.enum(DVH_CHAINS);

export const SALES_TYPES = [
  "field",
  "inside",
  "key_account",
  "ecommerce",
  "mixed",
] as const;
export type SalesType = (typeof SALES_TYPES)[number];
export const SalesTypeSchema = z.enum(SALES_TYPES);

export const BusinessOrganisationSchema = z.object({
  unitName: z.string().trim().min(1).max(120),
  unitNumber: z.string().trim().min(1).max(40),
  headcount: z.number().int().min(1).max(100_000),
  /** DVH only. */
  chain: DvhChainSchema.optional(),
  /** Sales only. */
  salesType: SalesTypeSchema.optional(),
  /** DVH only: the root store team created or adopted for this organisation. */
  storeTeamId: z.string().min(1).optional(),
});
export type BusinessOrganisation = z.infer<typeof BusinessOrganisationSchema>;

export const SaveBusinessOrganisationInput = z.object({
  businessId: z.string().min(1),
  unitName: z.string().trim().min(1).max(120),
  unitNumber: z.string().trim().min(1).max(40),
  headcount: z.number().int().min(1).max(100_000),
  chain: DvhChainSchema.optional(),
  salesType: SalesTypeSchema.optional(),
  /** The admin's mobile from the account step; copied onto their profile. */
  mobile: z.string().trim().max(32).optional(),
});
export type SaveBusinessOrganisationInput = z.infer<
  typeof SaveBusinessOrganisationInput
>;

/** `storeMeta.chain` for a DVH chain choice: every ICA profile is the ICA chain. */
export function storeChainFromDvhChain(chain: DvhChain): "ica" | "other" {
  return chain === "other" ? "other" : "ica";
}

/**
 * Which of `chain` / `salesType` a vertical requires. Returns the missing
 * field, or null when the input fits the vertical.
 */
export function missingOrganisationFieldForVertical(
  vertical: BusinessVertical,
  input: { chain?: DvhChain; salesType?: SalesType },
): "chain" | "salesType" | null {
  if (vertical === "dvh") return input.chain ? null : "chain";
  return input.salesType ? null : "salesType";
}

export const OrganisationVerticalSchema = BusinessVerticalSchema;
